import React, { useState } from 'react';
import { parseCsv } from './csv';
import type { CaptureProps, ImportAdapter, ImportedItem } from './types';

/** Vault fields the spreadsheet importer maps to. TOTP is intentionally excluded. */
export type VaultField = 'name' | 'url' | 'username' | 'password' | 'content' | 'group';

/** Maps each vault field to a CSV column index. Omitted = not mapped. */
export type Mapping = Partial<Record<VaultField, number>>;

/** Field priority for resolving "this column could map to multiple fields" conflicts. */
export const FIELD_ORDER: VaultField[] = [
  'name',
  'url',
  'username',
  'password',
  'content',
  'group',
];

/** Alias lookup. Matches are case-insensitive and trimmed. */
const ALIASES: Record<VaultField, string[]> = {
  name:     ['name', 'title', 'item name', 'site name', 'label'],
  url:      ['url', 'website', 'site', 'web address', 'login url', 'link', 'domain'],
  username: ['username', 'user name', 'user', 'login', 'email', 'account', 'login name'],
  password: ['password', 'pass', 'pwd', 'pw'],
  content:  ['notes', 'note', 'comment', 'comments', 'description', 'extra', 'memo'],
  group:    ['folder', 'group', 'category', 'collection', 'tag', 'tags'],
};

/**
 * Pre-fill the mapping dropdowns from CSV headers.
 *
 * Algorithm, per FIELD_ORDER:
 *   1. Try an exact (case-insensitive, trimmed) match against the field's aliases.
 *   2. If none, try a substring match (alias appears inside the header).
 *   3. A column is assigned to at most one field — earlier fields win.
 */
export function autoDetectMapping(headers: string[]): Mapping {
  const normalized = headers.map(h => h.trim().toLowerCase());
  const taken = new Set<number>();
  const mapping: Mapping = {};

  // Pass 1: exact matches
  for (const field of FIELD_ORDER) {
    const aliases = ALIASES[field];
    for (let i = 0; i < normalized.length; i++) {
      if (taken.has(i)) continue;
      if (aliases.includes(normalized[i])) {
        mapping[field] = i;
        taken.add(i);
        break;
      }
    }
  }

  // Pass 2: substring matches for fields still unmapped
  for (const field of FIELD_ORDER) {
    if (mapping[field] !== undefined) continue;
    const aliases = ALIASES[field];
    for (let i = 0; i < normalized.length; i++) {
      if (taken.has(i)) continue;
      if (aliases.some(a => normalized[i].includes(a))) {
        mapping[field] = i;
        taken.add(i);
        break;
      }
    }
  }

  return mapping;
}

/**
 * Walk every data row and turn it into an `ImportedItem` using the mapping.
 *
 * - Optional fields are omitted when the mapped cell is empty OR the field is
 *   not in the mapping.
 * - `name` resolves as: mapped name → mapped URL → "Untitled".
 * - Rows where every mapped field is empty are dropped.
 * - Whitespace around each cell is trimmed.
 */
export function applyMapping(rows: string[][], mapping: Mapping): ImportedItem[] {
  const get = (cells: string[], field: VaultField): string => {
    const idx = mapping[field];
    if (idx === undefined) return '';
    return (cells[idx] ?? '').trim();
  };

  const items: ImportedItem[] = [];

  for (const cells of rows) {
    const name     = get(cells, 'name');
    const url      = get(cells, 'url');
    const username = get(cells, 'username');
    const password = get(cells, 'password');
    const content  = get(cells, 'content');
    const group    = get(cells, 'group');

    // Skip rows where every mapped field is empty
    if (!name && !url && !username && !password && !content && !group) continue;

    const item: ImportedItem = {
      type: 'login',
      name: name || url || 'Untitled',
      sensitive: false,
      favorite: false,
      tags: [],
      importSource: 'spreadsheet',
    };

    if (url)      item.url      = url;
    if (username) item.username = username;
    if (password) item.password = password;
    if (content)  item.content  = content;
    if (group)    item.group    = group;

    items.push(item);
  }

  return items;
}

const EXPORT_INSTRUCTIONS: { id: string; label: string; steps: string }[] = [
  { id: 'gsheets',     label: 'Google Sheets',    steps: 'File → Download → Comma-separated values (.csv)' },
  { id: 'excel',       label: 'Excel',            steps: 'File → Save As → CSV UTF-8 (Comma delimited) (.csv)' },
  { id: 'numbers',     label: 'Numbers',          steps: 'File → Export To → CSV…' },
  { id: 'libreoffice', label: 'LibreOffice Calc', steps: 'File → Save As → Text CSV (.csv)' },
];

const FIELD_LABELS: Record<VaultField, string> = {
  name:     'Name',
  url:      'URL',
  username: 'Username',
  password: 'Password',
  content:  'Notes',
  group:    'Group',
};

function SpreadsheetCapture({ onCapture, onError }: CaptureProps) {
  const [screen, setScreen] = useState<'file' | 'map'>('file');
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Mapping>({});
  const [showHelp, setShowHelp] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(EXPORT_INSTRUCTIONS[0].id);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      onError('Please upload a CSV file (.csv).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(reader.result as string);
      if (rows.length < 2) {
        onError("We couldn't read any rows from this file.");
        return;
      }
      const [hdr, ...rest] = rows;
      setHeaders(hdr);
      setDataRows(rest);
      setMapping(autoDetectMapping(hdr));
      setScreen('map');
    };
    reader.onerror = () => onError('Failed to read file.');
    reader.readAsText(file);
  }

  function handleSelect(field: VaultField, value: string) {
    setMapping(prev => {
      const next = { ...prev };
      if (value === '') {
        delete next[field];
      } else {
        next[field] = Number(value);
      }
      return next;
    });
  }

  function handleContinue() {
    const items = applyMapping(dataRows, mapping);
    onCapture(JSON.stringify(items));
  }

  function previewFor(field: VaultField): string {
    const idx = mapping[field];
    if (idx === undefined || dataRows.length === 0) return '';
    const raw = (dataRows[0][idx] ?? '').trim();
    if (field === 'password' && raw) return '••••••••';
    return raw;
  }

  if (screen === 'file') {
    return (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => setShowHelp(s => !s)}
          aria-expanded={showHelp}
          aria-controls="spreadsheet-help-panel"
          className="self-start text-xs text-brand hover:text-brand-hover"
        >
          {showHelp ? '▾' : '▸'} Need help exporting?
        </button>

        {showHelp && (
          <div id="spreadsheet-help-panel" className="border border-divider rounded-xl p-4">
            <div className="flex gap-2 mb-3 flex-wrap">
              {EXPORT_INSTRUCTIONS.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`px-3 py-1 text-xs rounded-lg ${
                    activeTab === t.id
                      ? 'bg-hbs-primary text-hbs-on-primary'
                      : 'bg-surface text-ink-secondary hover:bg-surface-raised'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-secondary">
              {EXPORT_INSTRUCTIONS.find(t => t.id === activeTab)?.steps}
            </p>
          </div>
        )}

        <div className="flex flex-col items-center gap-4 p-6 border-2 border-dashed border-divider rounded-xl">
          <svg className="w-10 h-10 text-ink-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-ink-secondary text-center">
            Upload a CSV exported from your spreadsheet.
          </p>
          <label className="cursor-pointer px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-hbs-on-primary text-sm font-medium rounded-lg transition-colors">
            Choose CSV file
            <input type="file" accept=".csv" className="hidden" onChange={handleFile} />
          </label>
        </div>
      </div>
    );
  }

  // screen === 'map'
  const nameMapped = mapping.name !== undefined;
  const rowCount = dataRows.length;

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setScreen('file')}
        className="self-start text-xs text-ink-secondary hover:text-ink flex items-center gap-1"
      >
        ← Back
      </button>

      <div>
        <h2 className="text-base font-semibold text-ink">Match your columns</h2>
        <p className="text-sm text-ink-secondary mt-0.5">
          We&apos;ll save these fields to your safe. Pick which spreadsheet column each one comes from.
        </p>
      </div>

      <div className="border border-divider rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface border-b border-divider">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Vault field</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Source column</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-ink-secondary">Preview (first row)</th>
            </tr>
          </thead>
          <tbody>
            {FIELD_ORDER.map(field => (
              <tr key={field} className="border-b border-divider-subtle last:border-0">
                <td className="px-3 py-2 text-ink">
                  {FIELD_LABELS[field]}
                  {field === 'name' && <span className="text-red-400 ml-0.5" aria-label="required">*</span>}
                </td>
                <td className="px-3 py-2">
                  <select
                    value={mapping[field] ?? ''}
                    onChange={e => handleSelect(field, e.target.value)}
                    className="bg-surface text-ink text-sm rounded-lg border border-divider px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand"
                  >
                    <option value="">— not set —</option>
                    {headers.map((h, i) => (
                      <option key={i} value={i}>{h}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-ink-secondary text-xs">
                  {previewFor(field)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={handleContinue}
        disabled={!nameMapped}
        title={nameMapped ? '' : 'Pick a column to use as the item name.'}
        className="self-end px-6 py-2.5 bg-hbs-primary hover:bg-hbs-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-hbs-on-primary text-sm font-medium rounded-lg transition-colors"
      >
        Continue ({rowCount} row{rowCount !== 1 ? 's' : ''})
      </button>
    </div>
  );
}

export const SpreadsheetAdapter: ImportAdapter = {
  id: 'spreadsheet',
  name: 'Spreadsheet CSV',
  description: 'Import from a CSV exported from Google Sheets, Excel, Numbers, or LibreOffice.',
  CaptureComponent: SpreadsheetCapture,
  parse: (rawInput: string) => JSON.parse(rawInput) as ImportedItem[],
};
